import { useEffect, useMemo, useRef, useState } from 'react';
import { useSessionStore, TranscriptEntry } from '../stores/sessionStore';
import type { TranscriptSource } from '@stream-buddy/shared';

type ActivityFilter = 'all' | 'direct' | 'chat' | 'alerts';

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

function getEntrySource(entry: TranscriptEntry): TranscriptSource {
  return entry.source ?? 'direct';
}

interface ActivityLogProps {
  assistantName?: string;
  isOverlayConnected?: boolean;
}

const filters: { id: ActivityFilter; label: string; description: string }[] = [
  { id: 'all', label: 'All', description: 'Every response' },
  { id: 'direct', label: 'Direct', description: 'Streamer and manual turns' },
  { id: 'chat', label: 'Chat', description: 'Chat-triggered responses' },
  { id: 'alerts', label: 'Alerts', description: 'Donations, subs, bits, and event reactions' },
];

export function ActivityLog({ assistantName = 'Buddy', isOverlayConnected = false }: ActivityLogProps) {
  const {
    transcript,
    clearTranscript,
    isAiSpeaking,
    isAiThinking,
    isConnected,
    isChatConnected,
    isGeminiConnected,
    messagesAnalyzed,
    alertsTriggered,
  } = useSessionStore();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeFilter, setActiveFilter] = useState<ActivityFilter>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const [isHovering, setIsHovering] = useState(false);

  const entriesWithSource = useMemo(
    () => transcript.map((entry) => ({ entry, source: getEntrySource(entry) })),
    [transcript]
  );

  const filteredEntries = useMemo(() => {
    if (activeFilter === 'all') return entriesWithSource;
    const source = activeFilter === 'alerts' ? 'alert' : activeFilter;
    return entriesWithSource.filter((entry) => entry.source === source);
  }, [activeFilter, entriesWithSource]);

  const latestEntry = transcript[transcript.length - 1] ?? null;
  const featuredEntry = activeFilter === 'all' || activeFilter === 'direct'
    ? latestEntry
    : filteredEntries[filteredEntries.length - 1]?.entry ?? null;
  const timelineEntries = featuredEntry
    ? filteredEntries.filter(({ entry }) => entry.id !== featuredEntry.id)
    : filteredEntries;
  const alertCount = entriesWithSource.filter(({ source }) => source === 'alert').length;
  const chatResponseCount = entriesWithSource.filter(({ source }) => source === 'chat').length;
  const isLiveActive = isConnected || isAiSpeaking || isAiThinking;

  useEffect(() => {
    if (autoScroll && !isHovering && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [filteredEntries.length, isAiThinking, autoScroll, isHovering]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    setAutoScroll(scrollRef.current.scrollTop < 50);
  };

  const jumpToLatest = () => {
    setAutoScroll(true);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  return (
    <section className="h-full min-h-0 overflow-hidden rounded-[1.35rem] border border-cyan-300/[0.14] bg-[#050918]/[0.82] shadow-[0_24px_90px_rgba(0,0,0,0.48)] backdrop-blur-xl">
      <div className="flex h-full min-h-0 flex-col p-4 2xl:p-6">
        <div className="flex flex-col gap-3 border-b border-white/10 pb-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-1.5 flex items-center gap-2">
              <h2 className="text-2xl font-black tracking-tight text-white 2xl:text-3xl">Activity</h2>
              <span className={isAiSpeaking ? 'text-fuchsia-300' : 'text-cyan-300'}>
                <SparkIcon />
              </span>
            </div>
            <p className="text-sm font-medium text-slate-300">Real-time AI responses during your stream</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            {filters.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => setActiveFilter(filter.id)}
                className={`group flex h-10 items-center gap-2 rounded-xl border px-3.5 text-sm font-black transition-all ${
                  activeFilter === filter.id
                    ? 'border-violet-300/60 bg-gradient-to-r from-violet-500 to-blue-500 text-white shadow-[0_0_28px_rgba(139,92,246,0.28)]'
                    : 'border-white/10 bg-white/[0.035] text-slate-300 hover:border-cyan-300/40 hover:bg-cyan-300/10 hover:text-white'
                }`}
                title={filter.description}
              >
                <FilterIcon id={filter.id} />
                {filter.label}
              </button>
            ))}
            {transcript.length > 0 && (
              <button
                type="button"
                onClick={clearTranscript}
                className="ml-0 h-10 rounded-xl border border-white/10 bg-white/[0.035] px-3.5 text-sm font-black text-slate-300 transition-all hover:border-rose-300/50 hover:bg-rose-500/10 hover:text-white 2xl:ml-3"
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        <div className="grid flex-1 min-h-0 gap-4 pt-4 lg:grid-cols-[minmax(0,1fr)_18rem] 2xl:grid-cols-[minmax(0,1fr)_20rem] 2xl:gap-5 2xl:pt-5">
          <div className="flex min-h-0 flex-col">
            <FeaturedActivity
              assistantName={assistantName}
              latestEntry={featuredEntry}
              isAiSpeaking={isAiSpeaking}
              isAiThinking={isAiThinking}
            />

            <div
              ref={scrollRef}
              onScroll={handleScroll}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
              className="relative mt-3 min-h-0 flex-1 overflow-y-auto pr-1 2xl:mt-4"
            >
              {filteredEntries.length === 0 && !isAiThinking && !isAiSpeaking ? (
                <EmptyState activeFilter={activeFilter} assistantName={assistantName} />
              ) : timelineEntries.length === 0 ? (
                <div className="grid min-h-[5rem] place-items-center rounded-2xl border border-cyan-300/[0.10] bg-[#070c1b]/45 p-4 text-center">
                  <p className="text-sm font-bold text-slate-400">Earlier responses will appear here.</p>
                </div>
              ) : (
                <div className="space-y-3 pb-3">
                  {[...timelineEntries].reverse().map(({ entry, source }, index) => (
                    <div
                      key={entry.id}
                      data-testid="activity-history-item"
                      className={index === 0 ? '' : 'border-t border-white/[0.08] pt-3'}
                    >
                      <ActivityLogEntry
                        entry={entry}
                        source={source}
                        assistantName={assistantName}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-2 flex h-7 items-center justify-center gap-2 text-xs font-medium text-slate-400">
              {!autoScroll && (
                <button
                  type="button"
                  onClick={jumpToLatest}
                  className="rounded-full border border-cyan-300/25 bg-cyan-300/10 px-3 py-1 font-bold text-cyan-200 transition hover:border-cyan-200/70"
                >
                  Jump to latest
                </button>
              )}
              <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_16px_rgba(52,211,153,0.75)]" />
              <span>{autoScroll ? 'Live feed' : 'Viewing earlier activity'}</span>
            </div>
          </div>

          <aside className="grid min-h-0 content-start gap-3 overflow-y-auto lg:block lg:space-y-3 2xl:space-y-4">
            <StatusPanel
              assistantName={assistantName}
              isLiveActive={isLiveActive}
              isConnected={isConnected}
              isChatConnected={isChatConnected}
              isGeminiConnected={isGeminiConnected}
              isOverlayConnected={isOverlayConnected}
              isAiSpeaking={isAiSpeaking}
            />
            <SummaryPanel
              responses={transcript.length}
              alerts={Math.max(alertsTriggered, alertCount)}
              chatResponses={chatResponseCount}
              messagesAnalyzed={messagesAnalyzed}
            />
            <HelpPanel />
          </aside>
        </div>
      </div>
    </section>
  );
}

function FeaturedActivity({
  assistantName,
  latestEntry,
  isAiSpeaking,
  isAiThinking,
}: {
  assistantName: string;
  latestEntry: TranscriptEntry | null;
  isAiSpeaking: boolean;
  isAiThinking: boolean;
}) {
  const headline = isAiThinking
    ? `${assistantName} is preparing a response...`
    : latestEntry?.text ?? (isAiSpeaking ? `${assistantName} is speaking now...` : `Responses will appear here when ${assistantName} speaks.`);

  return (
    <div className={`relative overflow-hidden rounded-[1.2rem] border p-4 transition-all 2xl:rounded-[1.35rem] 2xl:p-5 ${
      isAiSpeaking
        ? 'border-fuchsia-300/75 bg-gradient-to-br from-[#1b0f3d] via-[#071026] to-[#062534] shadow-[0_0_42px_rgba(217,70,239,0.22)]'
        : latestEntry
          ? 'border-cyan-300/[0.18] bg-gradient-to-br from-[#111735] via-[#071024] to-[#092231]'
          : 'border-cyan-300/[0.12] bg-gradient-to-br from-[#0b1226] via-[#060b18] to-[#081c27]'
    }`}>
      <div className="pointer-events-none absolute -right-16 -top-24 h-56 w-56 rounded-full bg-fuchsia-500/[0.22] blur-3xl" />
      <div className="pointer-events-none absolute -left-10 bottom-0 h-32 w-64 rounded-full bg-cyan-400/[0.16] blur-3xl" />

      <div className="relative flex items-start gap-4">
        <div className={`grid h-14 w-14 shrink-0 place-items-center rounded-full border bg-gradient-to-br shadow-[0_0_30px_rgba(139,92,246,0.34)] 2xl:h-16 2xl:w-16 ${
          isAiSpeaking ? 'border-fuchsia-200/60 from-violet-500 to-cyan-400' : 'border-white/15 from-violet-500/60 to-blue-500/60'
        }`}>
          <AssistantFace />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-black">
            <span className={`rounded-md border px-2 py-1 ${
              isAiSpeaking
                ? 'border-rose-300/50 bg-rose-500/20 text-rose-100'
                : latestEntry
                  ? 'border-violet-300/30 bg-violet-500/10 text-violet-200'
                  : 'border-cyan-300/20 bg-cyan-400/10 text-cyan-100'
            }`}>
              {isAiSpeaking ? 'LIVE NOW' : isAiThinking ? 'THINKING' : latestEntry ? 'LATEST' : 'WAITING'}
            </span>
            <span className="text-violet-300">
              {latestEntry ? `${assistantName} response` : isAiSpeaking ? `${assistantName} is speaking` : isAiThinking ? 'Preparing response' : `${assistantName} is ready`}
            </span>
            {latestEntry && <span className="text-slate-400">{formatTime(latestEntry.timestamp)}</span>}
          </div>
          <p className="line-clamp-2 text-lg font-black leading-snug text-white 2xl:text-2xl">{headline}</p>
          {!latestEntry && !isAiSpeaking && !isAiThinking ? (
            <div className="mt-3 h-9 rounded-xl border border-cyan-300/[0.12] bg-cyan-300/[0.035] px-4 py-2 text-xs font-bold text-cyan-100/65 2xl:mt-4 2xl:h-12">
              Waiting for the first spoken response.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ActivityLogEntry({
  entry,
  source,
  assistantName,
}: {
  entry: TranscriptEntry;
  source: TranscriptSource;
  assistantName: string;
}) {
  const sourceMeta = getSourceMeta(source);

  return (
    <article className="group relative rounded-2xl border border-cyan-300/[0.12] bg-[#080d1e]/[0.86] p-4 shadow-[0_16px_38px_rgba(0,0,0,0.18)] transition-all hover:border-cyan-300/35 hover:bg-[#0a1730]/90">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-full border border-violet-300/25 bg-violet-500/15 text-violet-100">
          <SpeakerIcon />
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <span className="text-xs font-black text-violet-300">{assistantName} response</span>
            <span className="text-xs font-medium text-slate-500">{formatTime(entry.timestamp)}</span>
            <span className={`ml-auto rounded-full border px-3 py-1 text-xs font-black ${sourceMeta.badgeClass}`}>
              {sourceMeta.label}
            </span>
          </div>

          <p className="text-base font-medium leading-relaxed text-slate-100">{entry.text}</p>
        </div>
      </div>
    </article>
  );
}

function getSourceMeta(source: TranscriptSource): { label: string; badgeClass: string } {
  switch (source) {
    case 'alert':
      return {
        label: 'Alert',
        badgeClass: 'border-fuchsia-300/25 bg-fuchsia-500/10 text-fuchsia-200',
      };
    case 'chat':
      return {
        label: 'Chat',
        badgeClass: 'border-cyan-300/25 bg-cyan-500/10 text-cyan-200',
      };
    case 'direct':
    default:
      return {
        label: 'Direct',
        badgeClass: 'border-blue-300/25 bg-blue-500/10 text-blue-200',
      };
  }
}

function EmptyState({ activeFilter, assistantName }: { activeFilter: ActivityFilter; assistantName: string }) {
  const label = filters.find((filter) => filter.id === activeFilter)?.label ?? 'Activity';
  return (
    <div className="grid h-full min-h-[11rem] place-items-center rounded-2xl border border-dashed border-cyan-300/[0.16] bg-[#070c1b]/70 p-5 text-center 2xl:min-h-[16rem] 2xl:p-8">
      <div>
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full border border-violet-300/25 bg-violet-500/10 text-violet-100 2xl:h-14 2xl:w-14">
          <SpeakerIcon />
        </div>
        <p className="text-base font-black text-white">{label} will appear here</p>
        <p className="mt-1 text-sm font-medium text-slate-400">Start a live session and {assistantName} responses will populate this feed.</p>
      </div>
    </div>
  );
}

function StatusPanel({
  assistantName,
  isLiveActive,
  isConnected,
  isChatConnected,
  isGeminiConnected,
  isOverlayConnected,
  isAiSpeaking,
}: {
  assistantName: string;
  isLiveActive: boolean;
  isConnected: boolean;
  isChatConnected: boolean;
  isGeminiConnected: boolean;
  isOverlayConnected: boolean;
  isAiSpeaking: boolean;
}) {
  const statusRows = [
    { label: `${assistantName} Online`, ready: isConnected },
    { label: 'OBS Browser Source', ready: isOverlayConnected },
    { label: 'Twitch Chat Connected', ready: isChatConnected },
    { label: 'Voice Engine Active', ready: isGeminiConnected },
    { label: isAiSpeaking ? 'AI Speaking' : 'AI Listening', ready: isLiveActive },
  ];
  const liveStatusReady = isLiveActive && isOverlayConnected;
  const statusNeedsOverlay = !isOverlayConnected;

  return (
    <div className="rounded-2xl border border-cyan-300/[0.12] bg-[#081122]/[0.88] p-3.5 shadow-[0_16px_38px_rgba(0,0,0,0.22)] 2xl:p-4">
      <div className="mb-3 flex items-start justify-between gap-3 2xl:mb-4">
        <div>
          <h3 className="text-lg font-black text-white">Live Status</h3>
          <p className={liveStatusReady ? 'text-sm font-bold text-emerald-300' : statusNeedsOverlay ? 'text-sm font-bold text-amber-200' : 'text-sm font-bold text-slate-400'}>
            {liveStatusReady ? 'All systems go' : statusNeedsOverlay ? 'Connect OBS overlay' : 'Ready when you go live'}
          </p>
        </div>
        <span className={`mt-1 h-2 w-2 rounded-full ${liveStatusReady ? 'bg-emerald-400 shadow-[0_0_16px_rgba(52,211,153,0.75)]' : statusNeedsOverlay ? 'bg-amber-300 shadow-[0_0_16px_rgba(251,191,36,0.7)]' : 'bg-slate-500'}`} />
      </div>

      <div className="space-y-2.5 2xl:space-y-3">
        {statusRows.map((row) => (
          <div key={row.label} className="flex items-center gap-3">
            <span className={`grid h-7 w-7 place-items-center rounded-full border text-xs font-black ${
              row.ready
                ? 'border-emerald-300/35 bg-emerald-400/15 text-emerald-200'
                : 'border-white/10 bg-white/5 text-slate-500'
            }`}>
              {row.ready ? <CheckIcon /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            </span>
            <span className="min-w-0 truncate text-sm font-bold text-slate-200">{row.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryPanel({
  responses,
  alerts,
  chatResponses,
  messagesAnalyzed,
}: {
  responses: number;
  alerts: number;
  chatResponses: number;
  messagesAnalyzed: number;
}) {
  const rows = [
    { label: 'AI Responses', value: responses },
    { label: 'Alert Responses', value: alerts },
    { label: 'Chat Responses', value: chatResponses },
    { label: 'Chat Interactions', value: messagesAnalyzed },
  ];

  return (
    <div className="rounded-2xl border border-cyan-300/[0.12] bg-[#081122]/[0.88] p-3.5 shadow-[0_16px_38px_rgba(0,0,0,0.22)] 2xl:p-4">
      <h3 className="text-lg font-black text-white">Stream Summary</h3>
      <p className="mb-3 text-sm font-medium text-slate-400 2xl:mb-4">Current session</p>
      <div className="space-y-2.5 2xl:space-y-3">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-4">
            <span className="text-sm font-bold text-slate-300">{row.label}</span>
            <span className="text-base font-black text-white">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HelpPanel() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-violet-300/[0.16] bg-gradient-to-br from-[#1a0d35]/80 via-[#081122]/[0.88] to-[#071a27]/[0.88] p-3.5 shadow-[0_16px_38px_rgba(0,0,0,0.22)] 2xl:p-4">
      <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-violet-500/25 blur-3xl" />
      <div className="relative">
        <h3 className="text-lg font-black text-white">Need help?</h3>
        <p className="mt-2 text-sm font-medium leading-relaxed text-slate-300">
          Quick tips and guides for running StreamBuddy during a live stream.
        </p>
        <button
          type="button"
          className="mt-4 text-sm font-black text-violet-300 transition hover:text-cyan-200"
        >
          View Help Center <span aria-hidden="true">-&gt;</span>
        </button>
      </div>
    </div>
  );
}

function FilterIcon({ id }: { id: ActivityFilter }) {
  if (id === 'all') return <span className="h-2 w-2 rounded-full bg-current" />;
  if (id === 'alerts') return <BellIcon />;
  if (id === 'chat') return <StarIcon />;
  return <SpeakerIcon />;
}

function SparkIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3.5 13.8 9l5.7 1.8-5.7 1.8L12 18l-1.8-5.4-5.7-1.8L10.2 9 12 3.5Z" fill="currentColor" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="m3.5 8.2 3 3 6-6.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SpeakerIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 9.5v5h3.5L13 19V5L7.5 9.5H4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6.5 10.5a5.5 5.5 0 0 1 11 0v3.1l1.6 2.7H4.9l1.6-2.7v-3.1Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M10 19a2.3 2.3 0 0 0 4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m12 3 2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6-4.5-4.2 6.1-.7L12 3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function AssistantFace() {
  return (
    <svg className="h-8 w-8 text-white" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8.5 11.5h.1M15.4 11.5h.1" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
      <path d="M8.8 15c1.8 1.6 4.6 1.6 6.4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export default ActivityLog;
